
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Candidate {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  status?: string;
  linkedin_url?: string;
  resume_url?: string;
  skills?: string[];
  experience_years?: number;
  location_city?: string;
  location_state?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  last_contact?: string;
  source?: string;
  salary_amount?: number;
  salary_currency?: string;
  user_id: string;
  start_date?: string;
  placement_fee?: number;
  company_rate?: number;
  deleted_at?: string;
  deleted_by?: string;
}

export const useCandidates = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const candidatesQuery = useQuery({
    queryKey: ['candidates', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('No authenticated user, returning empty candidates array');
        return [];
      }

      console.log('Fetching candidates for user:', session.user.id);

      // Query with explicit user_id filter for strict user isolation
      // RLS policies will also enforce this at database level
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('user_id', session.user.id) // Explicit user filtering
        .is('deleted_at', null) // Only get non-deleted candidates
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching candidates:', error);
        throw error;
      }

      console.log('Successfully fetched candidates:', data?.length || 0);
      return data as Candidate[];
    },
    enabled: !!session?.user?.id,
  });

  const updateCandidateStatusMutation = useMutation({
    mutationFn: async ({ candidateId, status }: { candidateId: string; status: string; oldStatus?: string }) => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('candidates')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', candidateId)
        .eq('user_id', session.user.id) // Ensure user can only update their own candidates
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });

  return {
    ...candidatesQuery,
    updateCandidateStatus: updateCandidateStatusMutation.mutate,
    isUpdatingStatus: updateCandidateStatusMutation.isPending,
  };
};
